import React, { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Sparkles } from 'lucide-react';
import { useAddToChat } from '../../../../../components/chat/chatContext';
import { isGameLive, isGameFinal } from '../../rtdb/rtdbConfig';
import XPicksButton from '../../../../../components/dashboards/components/shared/XPicksButton';
import PredictButton from '../../../../../components/dashboards/components/shared/PredictButton';
import ViewOddsButton from '../../../../../components/dashboards/components/shared/ViewOddsButton';
import PredictionBox from '../../../../../components/dashboards/components/shared/PredictionBox';

const NHLMobileGameCard = memo(({
  game,
  onViewOdds,
  isSelected = false,
  isFrozen = false,
  onToggleSelection,
  onViewPrediction,
  prediction = null,
  showPredictionButton = true,
  t = (text) => text
}) => {
  const navigate = useNavigate();
  const handleAddToChat = useAddToChat();

  // Handler to stage prediction and navigate to chat
  const handleAnalyzeInChat = async () => {
    if (!prediction) return;

    const message = `Analyze this NHL prediction:

${teams?.away?.name} @ ${teams?.home?.name}

Prediction: ${prediction.winner}${prediction.confidenceNumeric ? ` (${prediction.confidenceNumeric}% confidence)` : ''}
Confidence Level: ${prediction.confidence}
${prediction.reason ? `Reasoning: ${prediction.reason}` : ''}

Can you provide a deeper analysis of this matchup?`;

    await handleAddToChat(message);
    navigate('/chat');
  };

  if (!game) return null;

  const { teams, scores, status, date, league } = game;
  const statusShort = status?.short;
  const isLive = isGameLive(statusShort);
  const isFinished = isGameFinal(statusShort);
  const isUpcoming = !isLive && !isFinished;
  const predictLabel = t(
    isFrozen ? 'COOLDOWN' : isSelected ? 'Selected' : isLive ? 'Predict Live' : 'Predict'
  );

  // Get display text for status badge
  const getStatusDisplay = () => {
    if (isLive) {
      const periodMap = {
        'P1': '1st',
        'P2': '2nd',
        'P3': '3rd',
        'OT': 'OT',
        'PT': 'SO'
      };
      const period = periodMap[statusShort] || 'LIVE';
      if (game.timer) {
        return `${period} ${game.timer}`;
      }
      return `LIVE ${period}`;
    }
    if (isFinished) return 'FINAL';
    return null;
  };

  const statusDisplay = getStatusDisplay();

  // Format game time
  const formatTime = () => {
    if (isLive) return 'LIVE';
    if (isFinished) return 'Final';

    try {
      const gameDate = new Date(date);
      if (!isNaN(gameDate.getTime())) {
        const options = {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
          timeZone: 'America/New_York'
        };
        return `${gameDate.toLocaleTimeString('en-US', options)} ET`;
      }
    } catch (e) {
      return 'TBD';
    }

    return 'TBD';
  };

  return (
    <div
      className={`relative border border-white/10 p-4 bg-white/[0.03] transition-all`}
    >
      {/* Status Badge - Top Right */}
      {statusDisplay && (
        <div className="absolute top-2 right-2 z-10">
          <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${
            isLive ? 'bg-red-500' : 'bg-white/20'
          }`}>
            {isLive && <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />}
            <span className="text-[10px] font-bold text-white">
              {statusDisplay}
            </span>
          </div>
        </div>
      )}

      {/* League Label */}
      <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-2 truncate pr-16">
        {league?.name || 'NHL'}
      </div>

      {/* Teams and Scores */}
      <div className="space-y-2 mt-3">
        {/* Away Team */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {teams?.away?.logo && (
              <img
                src={teams.away.logo}
                alt={teams.away.name}
                className="w-9 h-9 object-contain border border-white/10 rounded-sm bg-black/40"
                loading="lazy"
              />
            )}
            <span className="text-sm font-mono text-white tracking-tight leading-tight truncate">
              {teams?.away?.name || 'Away Team'}
            </span>
          </div>
          <span className="text-xl font-black text-white ml-2 tabular-nums">
            {isLive || isFinished ? (scores?.away ?? '-') : '-'}
          </span>
        </div>

        {/* Home Team */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {teams?.home?.logo && (
              <img
                src={teams.home.logo}
                alt={teams.home.name}
                className="w-9 h-9 object-contain border border-white/10 rounded-sm bg-black/40"
                loading="lazy"
              />
            )}
            <span className="text-sm font-mono text-white tracking-tight leading-tight truncate">
              {teams?.home?.name || 'Home Team'}
            </span>
          </div>
          <span className="text-xl font-black text-white ml-2 tabular-nums">
            {isLive || isFinished ? (scores?.home ?? '-') : '-'}
          </span>
        </div>
      </div>

      {/* Game Info - Time */}
      {isUpcoming && (
        <div className="mt-3 pt-2 border-t border-white/10 flex items-center gap-1 text-white/60 text-[10px] font-semibold tracking-[0.22em] uppercase">
          <Clock size={10} />
          <span>{formatTime()}</span>
        </div>
      )}

      {/* Prediction Box */}
      {prediction && (
        <div className="pt-2">
          <PredictionBox
            prediction={prediction}
            onAnalyze={handleAnalyzeInChat}
            variant="mobile"
            t={t}
          />
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-3 grid grid-cols-3 gap-2 items-stretch">
        {(isUpcoming || isLive) && (
          <div className="col-span-1">
            <XPicksButton
              gameData={{
                homeTeam: teams?.home?.name,
                awayTeam: teams?.away?.name,
                homeTeamId: teams?.home?.id,
                awayTeamId: teams?.away?.id,
                leagueId: league?.id || 57,
                date: date,
                time: formatTime(),
                gameId: game.id
              }}
              sport="nhl"
              label={t('XPICKS')}
            />
          </div>
        )}

        {showPredictionButton && (isUpcoming || isLive) && onToggleSelection && (
          <div className="col-span-1">
            <PredictButton
              label={predictLabel}
              isSelected={isSelected}
              isLive={isLive}
              isFrozen={isFrozen}
              onClick={(e) => {
                e.stopPropagation();
                if (!isFrozen) {
                  onToggleSelection(game);
                  if (onViewPrediction && !isSelected) {
                    onViewPrediction(game);
                  }
                }
              }}
            />
          </div>
        )}

        {/* Disabled until integration with the API to show odds
        {onViewOdds && (
            <div className="col-span-1">
            <ViewOddsButton
              label={t('VIEW ODDS')}
              icon={Eye}
              onClick={() => onViewOdds(game)}
            />
          </div>
        )}
        */}
      </div>
    </div>
  );
});

NHLMobileGameCard.displayName = 'MobileNHLGameCard';

export default NHLMobileGameCard;
